import dayjs from 'dayjs';
import { CircleUserRound, ClipboardList } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { getResponderBadge, isMobileUnitUser, setSeverity, setStatus, translatedTitle } from '@/lib';
import { Badge } from '..';

export const IncidentTooltip = ({
  title,
  type,
  status,
  assignees,
  creationTimestamp,
  severity,
  latestResponder,
  incidentAssigneeStatusList,
  users,
}) => {
  const { t } = useTranslation();

  const parsedResponders = React.useMemo(() => {
    if (Array.isArray(assignees)) return assignees;
    try {
      return JSON.parse(assignees || '[]');
    } catch (e) {
      console.warn('Invalid assignees value:', e, assignees);
      return [];
    }
  }, [assignees]);

  const statusIdToDisplayNameMap = (incidentAssigneeStatusList || []).reduce((acc, status) => {
    acc[status.id] = status.display_name;
    return acc;
  }, {});

  const handleResponderStatus = id => {
    let filterResponders = latestResponder?.filter(responders => responders?.assignee?.id.split('@')[0] == id);
    return getResponderBadge(filterResponders[0], statusIdToDisplayNameMap);
  };

  const mobileUnitResponders = parsedResponders?.filter(responder => isMobileUnitUser(responder?.id, users));

  return (
    <div className="rounded-lg bg p-4 shadow-lg gap-2">
      <div className="flex flex-row items-center gap-2">
        <ClipboardList size={16} />
        <h3 className="font-semibold text">{translatedTitle(title)}</h3>
      </div>

      <div className="grid grid-cols-2 gap-4 text-sm">
        <div className="gap-1">
          <div className="font-light text-xs text-muted">{t('MAP_VIEW.INCIDENT_TOOLTIP.INCIDENTTYPE')}</div>
          <div className="text-primary">
            {t('INCIDENT_TYPES', { returnObjects: true }).find(item => item.value === type)?.label || type}
          </div>
        </div>
        <div className="gap-1">
          <div className="font-light text-xs text-muted">{t('MAP_VIEW.INCIDENT_TOOLTIP.REPORTEDDATE')}</div>
          <div className="text-primary">{dayjs(creationTimestamp * 1000).format('DD/MM/YYYY HH:mm')}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 text-sm">
        <div className="gap-1">
          <div className="font-light text-xs text-muted">{t('MAP_VIEW.INCIDENT_TOOLTIP.SEVERITY')}</div>
          <div className="flex items-center gap-1 text">{setSeverity(severity)}</div>
        </div>
        <div className="gap-1">
          <div className="font-light text-xs text-muted">{t('MAP_VIEW.INCIDENT_TOOLTIP.STATUS')}</div>
          <div>{setStatus(status)}</div>
        </div>
      </div>

      {/* Responder(s) Section */}
      <div className="text-sm text">
        <div className="font-light text-xs text-muted">{t('MAP_VIEW.INCIDENT_TOOLTIP.RESPONDERS')}</div>

        {mobileUnitResponders?.length > 0 ? (
          mobileUnitResponders?.map(responder => (
            <div key={responder?.id ?? responder?.user_id} className="flex items-center justify-between px-2 py-1.5">
              {/* Avatar + Name */}
              <div className="flex items-center gap-2">
                <CircleUserRound className="w-6 h-6 text" />
                <div>
                  <div className="text font-normal">{responder.display_name}</div>
                </div>
              </div>

              <Badge
                variant={handleResponderStatus(responder?.id).variant}
                aria-label={`remove-${responder.id ?? responder.user_id}`}
                size="sm"
                className="text-xs"
              >
                {t('RESPONDER_STATUS', { returnObjects: true }).find(
                  status => status.value === handleResponderStatus(responder?.id).label
                )?.label || handleResponderStatus(responder?.id).label}
              </Badge>
            </div>
          ))
        ) : (
          <p className="text text-sm">{t('MAP_VIEW.INCIDENT_TOOLTIP.NO_RESPONDERS')}</p>
        )}
      </div>
    </div>
  );
};
